import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import {
  Send,
  Building2,
  KeyRound,
  Users,
  Rocket,
  CheckCircle2,
  Loader2,
  ChevronRight,
  Upload,
} from "lucide-react";

const STEPS = [
  { key: "org", label: "Organization", icon: Building2 },
  { key: "whatsapp", label: "WhatsApp", icon: KeyRound },
  { key: "verify", label: "Verify", icon: CheckCircle2 },
  { key: "import", label: "Import Contacts", icon: Users },
  { key: "broadcast", label: "First Broadcast", icon: Rocket },
];

export default function Onboarding() {
  const { user, organization, refresh } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);

  // Step 1 state
  const [orgName, setOrgName] = useState("");
  const [industry, setIndustry] = useState("");

  // Step 2 state
  const [waSettings, setWaSettings] = useState({
    business_account_id: "",
    phone_number_id: "",
    access_token: "",
    verify_token: "flowsend_" + Math.random().toString(36).slice(2, 10),
  });

  // Step 4
  const [csvFile, setCsvFile] = useState(null);

  // Step 5
  const [broadcastName, setBroadcastName] = useState("Welcome Campaign");
  const [broadcastBody, setBroadcastBody] = useState(
    "Hi {{name}}! Thanks for joining us. Reply HELP for support."
  );

  const createOrg = async () => {
    if (!orgName) return toast.error("Organization name required");
    setLoading(true);
    try {
      await api.post("/auth/onboarding/organization", { name: orgName, industry });
      await refresh();
      toast.success("Organization created");
      setStep(1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setLoading(false);
    }
  };

  const saveWA = async () => {
    setLoading(true);
    try {
      await api.post("/whatsapp/settings", waSettings);
      toast.success("WhatsApp settings saved");
      setStep(2);
    } catch (e) {
      toast.error("Failed to save");
    } finally {
      setLoading(false);
    }
  };

  const verifyWA = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/whatsapp/verify");
      if (data.connected) {
        toast.success("Credentials verified!");
      } else {
        toast.info("Credentials not fully configured — you can connect later in Settings.");
      }
      setStep(3);
    } catch (e) {
      toast.info("You can configure WhatsApp later in Settings");
      setStep(3);
    } finally {
      setLoading(false);
    }
  };

  const importCsv = async () => {
    if (!csvFile) {
      toast.info("Skipping import");
      setStep(4);
      return;
    }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", csvFile);
      const { data } = await api.post("/contacts/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Imported ${data.created} contacts (${data.skipped} skipped)`);
      setStep(4);
    } catch (e) {
      toast.error("Import failed");
    } finally {
      setLoading(false);
    }
  };

  const seedAndFinish = async () => {
    setLoading(true);
    try {
      // Seed sample data so user has something to play with
      try { await api.post("/seed"); } catch (e) { /* ignore */ }

      // Create a draft broadcast
      await api.post("/broadcasts", {
        name: broadcastName,
        message_body: broadcastBody,
        audience: { type: "all", tag_ids: [], statuses: [], contact_ids: [] },
        status: "draft",
      });

      await api.post("/auth/onboarding/complete");
      toast.success("Onboarding complete!");
      navigate("/dashboard");
    } catch (e) {
      toast.error("Failed to complete");
    } finally {
      setLoading(false);
    }
  };

  const skipOnboarding = async () => {
    setLoading(true);
    try {
      if (!organization) {
        await api.post("/auth/onboarding/organization", {
          name: orgName || `${user?.name || "My"}'s Workspace`,
        });
      }
      try { await api.post("/seed"); } catch (e) { /* ignore */ }
      await api.post("/auth/onboarding/complete");
      await refresh();
      navigate("/dashboard");
    } catch (e) {
      toast.error("Failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 py-10 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center gap-2 justify-center mb-8">
          <div className="w-9 h-9 rounded-md bg-brand grid place-items-center text-white">
            <Send className="w-4 h-4" />
          </div>
          <span className="font-heading text-xl font-semibold text-slate-900">FlowSend</span>
        </div>

        {/* Stepper */}
        <div className="flex items-center justify-between mb-8 px-2">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            const active = i === step;
            const done = i < step;
            return (
              <div key={s.key} className="flex items-center flex-1">
                <div className="flex flex-col items-center flex-shrink-0">
                  <div
                    className={`w-9 h-9 rounded-full grid place-items-center transition-colors ${
                      active
                        ? "bg-brand text-white"
                        : done
                        ? "bg-emerald-100 text-emerald-700"
                        : "bg-slate-100 text-slate-400"
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                  </div>
                  <span
                    className={`text-[11px] mt-1.5 font-medium ${
                      active ? "text-slate-900" : "text-slate-500"
                    }`}
                  >
                    {s.label}
                  </span>
                </div>
                {i < STEPS.length - 1 && (
                  <div className={`flex-1 h-px mx-2 ${i < step ? "bg-emerald-400" : "bg-slate-200"}`} />
                )}
              </div>
            );
          })}
        </div>

        <Card className="border border-slate-200 shadow-none">
          <CardContent className="p-8">
            {step === 0 && (
              <div data-testid="onboarding-step-org">
                <h2 className="font-heading text-xl font-semibold text-slate-900">Set up your workspace</h2>
                <p className="text-sm text-slate-500 mt-1">
                  Each workspace is an isolated organization with its own contacts, broadcasts and flows.
                </p>
                <div className="mt-6 space-y-4">
                  <div>
                    <Label className="text-xs font-semibold tracking-wider uppercase text-slate-600">
                      Organization name
                    </Label>
                    <Input
                      data-testid="onboarding-org-name"
                      value={orgName}
                      onChange={(e) => setOrgName(e.target.value)}
                      placeholder="Acme Co"
                      className="mt-1.5"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold tracking-wider uppercase text-slate-600">
                      Industry (optional)
                    </Label>
                    <Input
                      value={industry}
                      onChange={(e) => setIndustry(e.target.value)}
                      placeholder="e.g. D2C, SaaS, Education"
                      className="mt-1.5"
                    />
                  </div>
                </div>
                <div className="mt-7 flex justify-end gap-2">
                  <Button
                    variant="ghost"
                    onClick={skipOnboarding}
                    data-testid="onboarding-skip-button"
                    disabled={loading}
                  >
                    Skip & use sample data
                  </Button>
                  <Button
                    onClick={createOrg}
                    data-testid="onboarding-org-continue"
                    disabled={loading}
                    className="bg-brand hover:bg-brand-dark"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (<>Continue <ChevronRight className="w-4 h-4 ml-1" /></>)}
                  </Button>
                </div>
              </div>
            )}

            {step === 1 && (
              <div data-testid="onboarding-step-whatsapp">
                <h2 className="font-heading text-xl font-semibold text-slate-900">Connect WhatsApp</h2>
                <p className="text-sm text-slate-500 mt-1">
                  You can skip this and add credentials later in Settings.
                </p>
                <div className="mt-6 space-y-4">
                  {[
                    { key: "business_account_id", label: "Business Account ID" },
                    { key: "phone_number_id", label: "Phone Number ID" },
                    { key: "access_token", label: "Permanent Access Token", type: "password" },
                    { key: "verify_token", label: "Webhook Verify Token" },
                  ].map((f) => (
                    <div key={f.key}>
                      <Label className="text-xs font-semibold tracking-wider uppercase text-slate-600">
                        {f.label}
                      </Label>
                      <Input
                        type={f.type || "text"}
                        value={waSettings[f.key]}
                        onChange={(e) => setWaSettings({ ...waSettings, [f.key]: e.target.value })}
                        className="mt-1.5"
                        data-testid={`onboarding-wa-${f.key}`}
                      />
                    </div>
                  ))}
                </div>
                <div className="mt-7 flex justify-end gap-2">
                  <Button variant="ghost" onClick={() => setStep(2)} disabled={loading}>
                    Skip for now
                  </Button>
                  <Button
                    onClick={saveWA}
                    data-testid="onboarding-wa-continue"
                    disabled={loading}
                    className="bg-brand hover:bg-brand-dark"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save & continue"}
                  </Button>
                </div>
              </div>
            )}

            {step === 2 && (
              <div data-testid="onboarding-step-verify">
                <h2 className="font-heading text-xl font-semibold text-slate-900">Verify connection</h2>
                <p className="text-sm text-slate-500 mt-1">
                  We'll ping Meta's API to confirm your credentials work.
                </p>
                <div className="mt-6 p-4 rounded-md bg-brand-light border border-[#A7F3D0] text-sm text-emerald-900">
                  <p className="font-semibold">Webhook URL:</p>
                  <code className="block mt-1 text-xs break-all">
                    {process.env.REACT_APP_BACKEND_URL}/api/whatsapp/webhook
                  </code>
                  <p className="mt-2 text-xs">
                    Configure this URL with your verify token in Meta App Dashboard → WhatsApp → Webhooks.
                  </p>
                </div>
                <div className="mt-7 flex justify-end">
                  <Button
                    onClick={verifyWA}
                    data-testid="onboarding-verify-button"
                    disabled={loading}
                    className="bg-brand hover:bg-brand-dark"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Verify & continue"}
                  </Button>
                </div>
              </div>
            )}

            {step === 3 && (
              <div data-testid="onboarding-step-import">
                <h2 className="font-heading text-xl font-semibold text-slate-900">Import your contacts</h2>
                <p className="text-sm text-slate-500 mt-1">
                  Upload a CSV with columns: <code>name, phone, email, city, country, lead_source</code>.
                </p>
                <div className="mt-6">
                  <label className="flex flex-col items-center justify-center border-2 border-dashed border-slate-200 rounded-lg p-8 cursor-pointer hover:border-brand transition-colors">
                    <Upload className="w-6 h-6 text-slate-400" />
                    <span className="text-sm text-slate-600 mt-2">
                      {csvFile ? csvFile.name : "Click to upload CSV"}
                    </span>
                    <input
                      type="file"
                      accept=".csv"
                      className="hidden"
                      data-testid="onboarding-csv-input"
                      onChange={(e) => setCsvFile(e.target.files[0])}
                    />
                  </label>
                </div>
                <div className="mt-7 flex justify-end gap-2">
                  <Button variant="ghost" onClick={() => setStep(4)} disabled={loading}>
                    Skip
                  </Button>
                  <Button
                    onClick={importCsv}
                    data-testid="onboarding-import-button"
                    disabled={loading}
                    className="bg-brand hover:bg-brand-dark"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Import & continue"}
                  </Button>
                </div>
              </div>
            )}

            {step === 4 && (
              <div data-testid="onboarding-step-broadcast">
                <h2 className="font-heading text-xl font-semibold text-slate-900">Create your first broadcast</h2>
                <p className="text-sm text-slate-500 mt-1">
                  This will be saved as a draft. You can edit and send it from the Broadcasts page.
                </p>
                <div className="mt-6 space-y-4">
                  <div>
                    <Label className="text-xs font-semibold tracking-wider uppercase text-slate-600">
                      Broadcast name
                    </Label>
                    <Input
                      value={broadcastName}
                      onChange={(e) => setBroadcastName(e.target.value)}
                      data-testid="onboarding-broadcast-name"
                      className="mt-1.5"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold tracking-wider uppercase text-slate-600">
                      Message
                    </Label>
                    <textarea
                      value={broadcastBody}
                      onChange={(e) => setBroadcastBody(e.target.value)}
                      data-testid="onboarding-broadcast-body"
                      rows={4}
                      className="mt-1.5 w-full rounded-md border border-slate-200 px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
                    />
                    <p className="text-xs text-slate-500 mt-1">Use {`{{name}}`} to personalize.</p>
                  </div>
                </div>
                <div className="mt-7 flex justify-end">
                  <Button
                    onClick={seedAndFinish}
                    data-testid="onboarding-finish-button"
                    disabled={loading}
                    className="bg-brand hover:bg-brand-dark"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Finish setup"}
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
